import { GenericEntity, GithubProjectEntity } from "@cohesive/api-entities";
import { EntityCardSkeleton, type Entity } from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { Kanban } from "lucide-react";
import React, { useMemo } from "react";
import { EntityDragRow } from "../../../dnd/EntityDragRow";
import { SelectorEmpty } from "../../Selector";
import { CompactListItem } from "../components/CompactListItem";

interface ProjectsListViewProps {
  projects: GithubProjectEntity[];
  isLoading: boolean;
  onProjectSelect: (project: GithubProjectEntity) => void;
  // Placement callback: receives the generic projection when available.
  onAddToContext: (entity: Entity) => void;
  genericById: Map<string, GenericEntity>;
}

export const ProjectsListView: React.FC<ProjectsListViewProps> = ({
  projects,
  isLoading,
  onProjectSelect,
  onAddToContext,
  genericById,
}) => {
  const projectIds = useMemo(
    () => projects.map((p) => String(p.data.id)),
    [projects],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <EntityCardSkeleton
            key={i}
            className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
          />
        ))}
      </div>
    );
  }

  if (projects.length === 0) {
    return <SelectorEmpty message="No projects found" />;
  }

  return (
    <div>
      {projects.map((project: GithubProjectEntity) => {
        const statusText = project.data.closed ? "Closed" : "Open";
        const updatedText = project.data.updated_at
          ? `Updated ${formatDistanceToNow(new Date(project.data.updated_at), {
              addSuffix: true,
            })}`
          : "";

        const subtitle = [statusText, updatedText].filter(Boolean).join(" · ");
        const generic = genericById.get(String(project.data.id));

        return (
          <EntityDragRow
            key={project.data.id}
            entity={project}
            generic={generic}
            entityId={String(project.data.id)}
            orderedIds={projectIds}
            onSelect={() => onAddToContext(generic ?? project)}
            dragScope="github"
          >
            <CompactListItem
              icon={<Kanban className="h-4 w-4" />}
              title={project.data.title}
              subtitle={subtitle}
              webLink={project.data.web_link}
              onClick={() => onProjectSelect(project)}
              onAddToContext={() => onAddToContext(generic ?? project)}
            />
          </EntityDragRow>
        );
      })}
    </div>
  );
};
